import {
  AccountDisabledError,
  AuthNetworkError,
  AuthUnavailableError,
  EmailNotVerifiedError,
  InvalidCredentialsError,
  InvalidPhoneNumberError,
  InvalidSecondFactorCodeError,
  RecentSignInRequiredError,
  SecondFactorInUseError,
  TooManyAttemptsError,
  TooManySecondFactorsError,
} from './authErrors';

/**
 * Both a wrong password and an unknown address land here once
 * email-enumeration protection is on — see InvalidCredentialsError.
 */
const INVALID_CREDENTIAL_CODES = new Set([
  'auth/invalid-credential',
  'auth/wrong-password',
  'auth/user-not-found',
  'auth/invalid-email',
]);

const INVALID_SECOND_FACTOR_CODE_CODES = new Set([
  'auth/invalid-verification-code',
  'auth/code-expired',
]);

/** Firebase's own throttling, and the project's SMS quota running out. */
const TOO_MANY_ATTEMPTS_CODES = new Set(['auth/too-many-requests', 'auth/quota-exceeded']);

/**
 * Not an error to the user: the password was right and a second factor is
 * next. firebaseAuthProvider must check for it before calling
 * mapFirebaseAuthError, which would otherwise report it as
 * AuthUnavailableError — "sign-in is temporarily unavailable" for every
 * enrolled user.
 */
export const MULTI_FACTOR_AUTH_REQUIRED_CODE = 'auth/multi-factor-auth-required';

export function firebaseErrorCode(error: unknown): string | null {
  if (typeof error !== 'object' || error === null || !('code' in error)) return null;
  return typeof error.code === 'string' ? error.code : null;
}

/**
 * Maps a Firebase Auth error to a typed error at the auth boundary. The
 * original `error.message` never crosses this function — see
 * AuthUnavailableError.
 */
export function mapFirebaseAuthError(error: unknown): Error {
  const code = firebaseErrorCode(error);

  if (code && INVALID_CREDENTIAL_CODES.has(code)) return new InvalidCredentialsError();
  if (code === 'auth/user-disabled') return new AccountDisabledError();
  if (code && TOO_MANY_ATTEMPTS_CODES.has(code)) return new TooManyAttemptsError();
  if (code === 'auth/network-request-failed') return new AuthNetworkError();
  if (code && INVALID_SECOND_FACTOR_CODE_CODES.has(code)) return new InvalidSecondFactorCodeError();
  if (code === 'auth/requires-recent-login') return new RecentSignInRequiredError();
  if (code === 'auth/unverified-email') return new EmailNotVerifiedError();
  if (code === 'auth/maximum-second-factor-count-exceeded') return new TooManySecondFactorsError();
  if (code === 'auth/second-factor-already-in-use') return new SecondFactorInUseError();
  if (code === 'auth/invalid-phone-number') return new InvalidPhoneNumberError();
  return new AuthUnavailableError(error);
}
